import { Dispatch, SetStateAction } from 'react';
import { Control, FieldErrorsImpl, RegisterOptions } from 'react-hook-form';
import { AGENCY_STATUS_OPTIONS, AGENCY_TEAM_OPTIONS } from '../constants/constants';
import { IOption } from '@cp/src/components/FilterDrawer/types';
import { AgentFormValues } from '../../AllAgents/AgentForm/interfaces/types';

export interface AddAgencyDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
}

export enum AgencyStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  TERMINATED = 'TERMINATED'
}

export interface AgencyFormProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  isEditDisabled?: boolean;
  setIsSubmitButtonDisabled: Dispatch<SetStateAction<boolean>>;
  isEditAgencyPage?: boolean;
  showAgencyPage?: boolean;
  setIsDirtyField?: Dispatch<SetStateAction<boolean>>;
}
export interface AgencyFormValues {
  agencyName: string;
  agencyCode: string;
  oracleId: string;
  serviceableStates: Array<string>;
  agencyTypes: Array<string>;
  agencyTeam: string;
  oneManAgency: boolean;
  managerName: string;
  managerEmailAddress: string;
  managerPhoneNumber: string;
  status: string;
  nextMonthAvailability: boolean;
}

export enum VALIDATION_MESSAGE {
  AGENCY_NAME_MESSAGE = 'Please enter a name',
  AGENCY_CODE_MESSAGE = 'Please enter an agency code',
  TL_NAME_MESSAGE = 'Please enter a name',
  TL_PHONE_NUMBER_MESSAGE = 'Please enter a valid 10 digit phone number',
  TL_EMAIL_MESSAGE = 'Please enter a valid email address'
}

export const defaultValues = {
  agencyName: '',
  agencyCode: '',
  serviceableStates: [],
  oracleId: '',
  agencyTeam: '',
  oneManAgency: false,
  managerName: '',
  managerEmailAddress: '',
  managerPhoneNumber: ''
};

export interface AgencyTypeSubTypeMapping {
  agencyTypeReferenceId: string;
}

export interface CreateAgencyRequest {
  agencyName: string;
  agencyCode: string;
  oracleId: string;
  serviceableStates: Array<string>;
  agencyTeam: string;
  oneManAgency: false;
  managerName: string;
  managerEmailAddress: string;
  managerPhoneNumber: string;
  agencyTypeSubTypeMappings?: Array<AgencyTypeSubTypeMapping>;
}
export interface UpdateAgencyRequest extends CreateAgencyRequest {
  status: string;
  nextMonthAvailability: boolean;
}
export enum AgencyType {
  CALLING = 'CALLING',
  FIELD = 'FIELD',
  FIELD_CALLING = 'FIELD_CALLING',
  HUMAN_REMINDER = 'HUMAN_REMINDER',
  SKIP_TRACING = 'SKIP_TRACING'
}

export enum AgencyTeam {
  ONE_TO_THIRTY_FIELD = 'FIELD_1_30',
  THIRTY_PLUS_FIELD = 'FIELD_30_PLUS',
  OTHER = 'OTHERS'
}

export interface EditAgencyFormDrawerProps {
  onClose: () => void;
  isDrawerOpen: boolean;
}

export interface Agency {
  name?: string;
  code?: string;
  agencyTypes?: Record<string, string[]>;
  allocatedCount?: number;
  unallocatedCount?: number;
  referenceId?: string;
  active?: boolean;
  oracleId?: string;
  agencyTeam?: string;
  serviceableStates?: Array<string>;
  status?: string;
  nextMonthAvailability?: boolean;
  oneManAgency?: boolean;
}

export interface AgencyValue {
  referenceId?: string;
  name?: string;
}

export type ToggleTabsProps = {
  control: Control<any>;
  name: string;
  tabOptions: { label: string; value: boolean }[];
  handleToggleClick?: (value: boolean) => void;
  errors: Partial<FieldErrorsImpl<AgencyFormValues>> | Partial<FieldErrorsImpl<AgentFormValues>>;
  rules?: RegisterOptions;
  styles: CSSModuleClasses;
  validationMessage: string;
  isToggleDisabled?: boolean;
};

export type AgencyFormTextboxProps = {
  control: Control<AgencyFormValues, any>;
  name: keyof AgencyFormValues;
  rules: RegisterOptions;
  errors: Partial<FieldErrorsImpl<AgencyFormValues>>;
  isEditDisabled: boolean | undefined;
  styles: CSSModuleClasses;
  inputLabel: string;
  placeholder: string;
  validationMessage: string;
  isRequired?: boolean;
  isUpperCaseRequired?: boolean;
};
export type AgencyFormRadioProps = {
  control: Control<AgencyFormValues, any>;
  name: keyof AgencyFormValues;
  rules: RegisterOptions;
  errors: Partial<FieldErrorsImpl<AgencyFormValues>>;
  isEditDisabled: boolean | undefined;
  styles: CSSModuleClasses;
  inputLabel: string;
  validationMessage: string;
  isRequired?: boolean;
  options: typeof AGENCY_STATUS_OPTIONS;
  isDisabled?: boolean;
};

export type AgencyFormStatesDropdownProps = {
  control: Control<AgencyFormValues, any>;
  name: keyof AgencyFormValues;
  errors: Partial<FieldErrorsImpl<AgencyFormValues>>;
  rules: RegisterOptions;
  isEditDisabled: boolean | undefined;
  styles: CSSModuleClasses;
  validationMessage: string;
  options: IOption[];
};

export type AgencyTypeOptions = {
  referenceId?: string;
  name?: string;
  agencySubTypes?: [];
  value?: string;
  label?: string;
};

export type AgencyFormChipProps = {
  control: Control<AgencyFormValues, any>;
  name: keyof AgencyFormValues;
  rules: RegisterOptions;
  errors: Partial<FieldErrorsImpl<AgencyFormValues>>;
  isEditDisabled: boolean | undefined;
  styles: CSSModuleClasses;
  inputLabel: string;
  validationMessage: string;
  isRequired?: boolean;
  inputOptions: typeof AGENCY_TEAM_OPTIONS | AgencyTypeOptions[];
  isMultipleSelectionAllowed?: boolean;
};

export interface EditAgencyDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  toggleOutsideClick: () => void;
  isDirtyField: boolean;
  isOutsideClick: boolean;
  handleSubmitClick: () => void;
  isSubmitButtonDisabled: boolean;
  handleExitYesClick: () => void;
  handleExitNoClick: () => void;
  section?: JSX.Element;
}

export interface FooterComponentProps {
  onClose: () => void;
  toggleOutsideClick: () => void;
  isDirtyField: boolean;
  isOutsideClick: boolean;
  handleSubmitClick: () => void;
  isSubmitButtonDisabled: boolean;
  handleExitYesClick: () => void;
  handleExitNoClick: () => void;
  isEditAgencyPage: boolean;
}
